import { useState, useEffect, useCallback } from 'react';
import { searchSongs, getGenreFromDeezer } from '../api/deezer';
import { createSongRequest, getDJInfo } from '../api/supabase';

export const useSongRequest = (formId) => {
    const [query, setQuery] = useState('');
    const [tableNumber, setTableNumber] = useState('');
    const [tableNumberError, setTableNumberError] = useState('');
    const [results, setResults] = useState([]);
    const [selectedSong, setSelectedSong] = useState(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [djInfo, setDjInfo] = useState({});
    const [loadingDJInfo, setLoadingDJInfo] = useState(true);

    useEffect(() => {
        const fetchDJInfo = async () => {
            try {
                const info = await getDJInfo(formId);
                setDjInfo(info);
            } catch (error) {
                console.error('Error fetching DJ info:', error);
            } finally {
                setLoadingDJInfo(false);
            }
        };
        fetchDJInfo();
    }, [formId]);

    const handleSearch = useCallback(async () => {
        if (!query.trim()) {
            setError('El campo de búsqueda no puede estar vacío');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const songs = await searchSongs(query);
            setResults(songs);
        } catch (error) {
            console.error('Error searching songs:', error);
            setError('Hubo un error al buscar canciones. Por favor, inténtelo de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [query]);

    const handleSelect = useCallback((song) => {
        setSelectedSong(song);
        setResults([]);
        setQuery('');
    }, []);

    const validateTableNumber = useCallback((number) => {
        const regex = /^\d+$/;
        if (!regex.test(number)) {
            return 'Por favor, ingresa un número de mesa válido (solo números)';
        }
        return '';
    }, []);

    const isTableNumberValid = useCallback(() => {
        if (formId !== 'fm0924tom') return true;
        return validateTableNumber(tableNumber.trim()) === '';
    }, [formId, tableNumber, validateTableNumber]);

    const handleTableNumberChange = useCallback((e) => {
        const number = e.target.value;
        setTableNumber(number);
        const error = validateTableNumber(number);
        setTableNumberError(error);
    }, [validateTableNumber]);

    const handleSubmit = useCallback(async () => {
        if (!selectedSong) return;
        if (formId === 'fm0924tom' && !isTableNumberValid()) {
            setTableNumberError('Por favor, ingresa un número de mesa válido');
            return;
        }

        setLoading(true);
        try {
            const genreData = await getGenreFromDeezer(
                selectedSong.artist.id,
                selectedSong.album.id
            );

            await createSongRequest(
                formId,
                selectedSong.title,
                selectedSong.artist.name,
                genreData.primary.join(', '),
                selectedSong.album.cover,
                formId === 'fm0924tom' ? tableNumber.trim() : null
            );

            setOpen(true);
        } catch (error) {
            console.error('Error al agregar la canción:', error);
            setError('Hubo un error al solicitar la canción. Por favor, inténtelo de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [formId, selectedSong, tableNumber, isTableNumberValid]);

    const handleCancel = useCallback(() => {
        setSelectedSong(null);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        setSelectedSong(null);
    }, []);

    return {
        query,
        setQuery,
        tableNumber,
        tableNumberError,
        results,
        selectedSong,
        open,
        loading,
        error,
        djInfo,
        loadingDJInfo,
        handleSearch,
        handleSelect,
        handleTableNumberChange,
        handleSubmit,
        handleCancel,
        handleClose
    };
};
